import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from "@angular/common/http";
import { SessionValues } from "./sessionvalues.service";
import { TransactionResult } from "../classes/infrastructure/transactionresult";
import { TransportData } from "../classes/infrastructure/transportdata";
import { DataContainerService } from "../classes/infrastructure/datacontainer.service";
import { ServiceInjector } from "../classes/infrastructure/injector";
import { PayloadPacket } from "../classes/infrastructure/payloadpacket/payloadpacket";
import { FileTransferObject } from "../classes/infrastructure/filetransferobject";
import { UserLogoutRequest } from "../classes/infrastructure/request_response/userlogoutrequest";
import { UserLoginResponse } from "../classes/infrastructure/request_response/userloginresponse";
import { UserLoginRequest } from "../classes/infrastructure/request_response/userloginrequest";
import { AppStateManageService } from "./app-state-manage.service";
import { RequestTypes } from "../classes/infrastructure/enums";
import { ChapterStateManagement } from "./chapterstatemanagement";
import { isNullOrUndefined } from "../../tools";
import { firstValueFrom, from } from "rxjs";
import { FinancialYearMonthRequest } from "../classes/infrastructure/request_response/financialyearmonthrequest";
import { FinancialYearMonthResponse } from "../classes/infrastructure/request_response/financialyearmonthlistresponse";
import { GlobalLoaderService } from "./global_loader.service";
import {
  GetValidMenuItemResponse,
  NavItemEntityResponse,
} from "../classes/infrastructure/request_response/navitementityauthority";
import { Router } from "@angular/router";
import Swal from "sweetalert2";
import { SidebarMenuStateService } from "./sidebarmenustateservice";
import { NgZone } from "@angular/core";
import { TranslateTextRequest } from "../classes/infrastructure/request_response/translatetextrequest";
import { TranslateTextResponse } from "../classes/infrastructure/request_response/translatetextresponse";
import { UserLanguagePreferenceRequest } from "../classes/infrastructure/request_response/updateuserlanguagepreference";
import { UpdateUserLanguageResponse } from "../classes/infrastructure/request_response/updateuserlanguagepreferenceresponse";

@Injectable({
  providedIn: "root",
})
export class ServerCommunicatorService {
  public static GetInstance(): ServerCommunicatorService {
    return ServiceInjector.AppInjector.get(ServerCommunicatorService);
  }

  constructor(
    private http: HttpClient,
    private sessionValues: SessionValues,
    private appStateManagement: AppStateManageService,
    private ngZone: NgZone,
    private chapterstatemanagement: ChapterStateManagement,
    private sidebarMenuState: SidebarMenuStateService,
    private loader: GlobalLoaderService,
    private router: Router,
  ) {}

  public async FetchResult(
    apiRoot: string,
    method: string,
  ): Promise<TransactionResult> {
    this.loader.show();
    try {
      let url = `${apiRoot}/${method}`;
      let request = this.http.get(url);

      let pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch((err) => {
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message: this.BuildErrorMessage(err)
          };

          return {
            Ref: 1,
            PartNo: 1,
            TotalPartCount: 1,
            Sender: this.sessionValues.CurrentLoginToken,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
            CoordinationId: 0,
          };
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
      };

      return result;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  private static readonly DocumentGenerationCodeCollectionName =
    "DocumentGenerationRequestCode";

  public async GetDocumentGenerationRequestCode(
    req: PayloadPacket,
  ): Promise<TransactionResult> {
    let tr = await this.sendHttpRequest(req);
    if (!tr.Successful) return tr;

    let code = "";

    let td = JSON.parse(tr.Tag) as TransportData;
    let coll = DataContainerService.GetInstance().GetOrCreateCollection(
      td.MainData,
      ServerCommunicatorService.DocumentGenerationCodeCollectionName,
    )!;
    for (let obj of coll.Entries) {
      code = (obj as any)["Code"] as string;
    }

    tr.Tag = code;

    return tr;
  }

  public async sendHttpRequest(
    pkt: PayloadPacket,
    method: string = "acceptrequest",
    files: FileTransferObject[] = [],
  ): Promise<TransactionResult> {
    // ✅ Show loader at start
    this.loader.show();
    try {
      let pktResult: PayloadPacket = null as any;
      // console.log(pkt);
      if (pkt.Payload.RequestType === RequestTypes.Fetch)
        method = "FetchAcceptRequest";
      else if (pkt.Payload.RequestType === RequestTypes.CustomProcess)
        method = "CustomAcceptRequest";
      else if (pkt.Payload.RequestType === RequestTypes.Deletion)
        method = "DeleteAcceptRequest";
      else if (pkt.Payload.RequestType === RequestTypes.Save)
        method = "SaveAcceptRequest";

      let url = `${this.sessionValues.requestController}/${method}`;
      let strPkt = JSON.stringify(pkt);

      const formData = new FormData();

      let fileIndex = 0;

      for (let file of files) {
        if (!isNullOrUndefined(file)) {
          if (file.id.length === 0) {
            fileIndex++;
            // formData.append(
            //  ' `file_${fileIndex}`',
            //   file.rawFile as Blob,
            //   file.name
            // );
            formData.append("ProfilePicFile", file.rawFile as Blob, file.name);
          } else {
            formData.append(file.id, file.rawFile as Blob, file.name);
          }
        }
      }

      formData.append("info", strPkt);

      let request = this.http.post(url, formData);

      pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch(async (err) => {
          // debugger
          await this.handleAuthError(err);

          // 🔹 Other Errors
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message: this.BuildErrorMessage(err)
          };

          return {
            Pid: 0,
            PartNo: 1,
            TotalPartCount: 1,
            Sender: 1001,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
          };
          // }
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
      };

      return result;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  //==================================== API For Dashboard without Loader start ==============================================
  public async sendHttpRequestWithoutLoader(
    pkt: PayloadPacket,
    method: string = "acceptrequest",
    files: FileTransferObject[] = [],
  ): Promise<TransactionResult> {
    // ✅ Show loader at start
    // this.loader.show();
    try {
      let pktResult: PayloadPacket = null as any;
      // console.log(pkt);
      if (pkt.Payload.RequestType === RequestTypes.Fetch)
        method = "FetchAcceptRequest";
      else if (pkt.Payload.RequestType === RequestTypes.CustomProcess)
        method = "CustomAcceptRequest";
      else if (pkt.Payload.RequestType === RequestTypes.Deletion)
        method = "DeleteAcceptRequest";
      else if (pkt.Payload.RequestType === RequestTypes.Save)
        method = "SaveAcceptRequest";

      let url = `${this.sessionValues.requestController}/${method}`;
      let strPkt = JSON.stringify(pkt);

      const formData = new FormData();

      let fileIndex = 0;

      for (let file of files) {
        if (!isNullOrUndefined(file)) {
          if (file.id.length === 0) {
            fileIndex++;
            // formData.append(
            //  ' `file_${fileIndex}`',
            //   file.rawFile as Blob,
            //   file.name
            // );
            formData.append("ProfilePicFile", file.rawFile as Blob, file.name);
          } else {
            formData.append(file.id, file.rawFile as Blob, file.name);
          }
        }
      }

      formData.append("info", strPkt);

      let request = this.http.post(url, formData);

      pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch(async (err) => {
          // debugger
          await this.handleAuthError(err);

          // 🔹 Other Errors
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message : this.BuildErrorMessage(err)
          };

          return {
            Pid: 0,
            PartNo: 1,
            TotalPartCount: 1,
            Sender: 1001,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
          };
          // }
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
      };

      return result;
    }
    // finally {
    //   // ✅ Hide loader at end, always runs
    //   this.loader.hide();
    // }
  }

  public async sendHttpRequestWithoutLoginToken(
    pkt: PayloadPacket,
    method: string = "sendhttprequestwithoutlogintoken",
    files: FileTransferObject[] = [],
  ): Promise<TransactionResult> {
    try {
      let pktResult: PayloadPacket = null as any;
      let url = `${this.sessionValues.requestController}/${method}`;
      let strPkt = JSON.stringify(pkt);

      const formData = new FormData();

      let fileIndex = 0;

      for (let file of files) {
        if (!isNullOrUndefined(file)) {
          if (file.id.length === 0) {
            fileIndex++;
            formData.append(
              `file_${fileIndex}`,
              file.rawFile as Blob,
              file.name,
            );
          } else {
            formData.append(file.id, file.rawFile as Blob, file.name);
          }
        }
      }

      formData.append("info", strPkt);

      let request = this.http.post(url, formData);

      pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch((err:HttpErrorResponse) => {
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message : this.BuildErrorMessage(err)

          };

          return {
            Pid: 0,
            Ref: 1,
            PartNo: 1,
            TotalPartCount: 1,
            // Sender: this.sessionValues.CurrentLoginToken,
            Sender: 1001,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
          };
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
      };

      return result;
    }

    return null as any;
  }
  //==================================== API For Dashboard without Loader End ================================================

  public async GetRefs(
    apiRoot: string,
    req: PayloadPacket,
  ): Promise<TransactionResult> {
    this.loader.show();
    try {
      let url = `${apiRoot}/allocateids`;
      let body = JSON.stringify(req);

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.post(url, body, { headers: headers });

      let pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch((err) => {
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message: this.BuildErrorMessage(err)
          };

          return {
            id: 1,
            PartNo: 1,
            TotalPartCount: 1,
            Sender: this.sessionValues.CurrentLoginToken,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
            CoordinationId: 0,
          };
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
        Tag: null,
        TagType: "",
      };

      return result;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  public async AllocateSingleIds(apiRoot: string): Promise<TransactionResult> {
    this.loader.show();
    try {
      // let loginToken = window.location.hostname.includes('gladiance.one')
      //                   || window.location.hostname.includes('localhost') ? 50000102 : 50000002

      let loginToken = this.sessionValues.CurrentLoginToken;
      let url = `${apiRoot}/allocatesingleid/${loginToken}`;
      let body = {};

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.post(url, body);

      let pktResult = await request
        .toPromise()
        .then((resp) => resp as any)
        .catch((err) => {
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message: this.BuildErrorMessage(err)
          };

          return;
          tr;
        });

      if (!pktResult.Successful) {
        let result = pktResult as TransactionResult;
        return result;
      }
      if (pktResult.Successful) {
        let result = pktResult as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
        Tag: null,
        TagType: "",
      };

      return result;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  public async GetProcessTokens(
    apiRoot: string,
    req: PayloadPacket,
  ): Promise<TransactionResult> {
    this.loader.show();
    try {
      let url = `${apiRoot}/allocateprocesstokens`;
      let body = JSON.stringify(req);

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.post(url, body, { headers: headers });

      let pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch((err) => {
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message: this.BuildErrorMessage(err)
          };

          return {
            Ref: 1,
            PartNo: 1,
            TotalPartCount: 1,
            Sender: this.sessionValues.CurrentLoginToken,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
          };
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
        Tag: null,
        TagType: "",
      };

      return result;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  public async GetCurrentDateTime(
    apiRoot: string,
    req: PayloadPacket,
  ): Promise<TransactionResult> {
    this.loader.show();
    try {
      let url = `${apiRoot}/getcurrentdatetime`;
      let body = JSON.stringify(req);

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.post(url, body, { headers: headers });

      let pktResult = await request
        .toPromise()
        .then((resp) => resp as PayloadPacket)
        .catch((err:HttpErrorResponse) => {
          let tr: TransactionResult = {
            Successful: false,
            // Message: err.statusText,
            Message: this.BuildErrorMessage(err)
          };

          return {
            Ref: 1,
            PartNo: 1,
            TotalPartCount: 1,
            Sender: this.sessionValues.CurrentLoginToken,
            Topic: "ServerToClientResponse",
            PayloadDescriptor: "TransactionResult",
            Payload: JSON.stringify(tr),
            TargetMethod: "",
          };
        });

      if (pktResult.PayloadDescriptor === "TransactionResult") {
        let result = JSON.parse(pktResult.Payload) as TransactionResult;
        return result;
      }

      return null as any;
    } catch (error) {
      let result: TransactionResult = {
        Successful: false,
        Message: error as any,
        Tag: null,
        TagType: "",
      };

      return result;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }
  // old code for logout custom call to invalidate token on server and then navigate to login page
  // public async LoginUser(req: UserLoginRequest) {
  //   let apiRoot = this.sessionValues.requestController;
  //   let url = `${apiRoot}/loginsystemuser`;

  //   const headers = new HttpHeaders({
  //     "Content-Type": "application/json",
  //   });

  //   let request = this.http.post(url, req, { headers: headers });
  //   let resp = await request
  //     .toPromise()
  //     .then((resp) => resp as any)
  //     .catch((err) => {
  //       let tr = new UserLoginResponse();
  //       console.log('tr :', tr);
  //       tr.successful = false;
  //       tr.message = err.statusText;
  //       return tr;
  //     });

  //   let result = Object.assign(
  //     new UserLoginResponse(),
  //     resp
  //   ) as UserLoginResponse;
  //   this.sessionValues.CurrentLoginToken = result.login_token;
  //   this.sessionValues.UserDisplayName = result.user_display_name;
  //   // this.appStateManagement.setValidMenuItemIds2(result.ValidMenuItemIds2);
  //   this.appStateManagement.StorageKey.setItem(
  //     "LoginUserRef",
  //     result.login_user_id.toString()
  //   );
  //   this.appStateManagement.StorageKey.setItem(
  //     "ToDate",
  //     result.to_date.toString()
  //   );
  //   this.appStateManagement.StorageKey.setItem("Role", result.role.toString());
  //   // this.appStateManagement.StorageKey.setItem('ChapterRef', result.ChapterRef.toString());
  //   // this.appStateManagement.StorageKey.setItem('SubChapterRef', result.SubChapterRef.toString());
  //   // this.appStateManagement.StorageKey.setItem('LastSelectedChapterRef', result.LastSelectedChapterRef.toString());
  //   // // this.appStateManagement.StorageKey.setItem('LastSelectedChapterName', result.LastSelectedChapterName);
  //   // this.appStateManagement.StorageKey.setItem('LastSelectedSubChapterRef', result.LastSelectedSubChapterRef.toString());
  //   // // this.appStateManagement.StorageKey.setItem('LastSelectedSubChapterName', result.LastSelectedSubChapterName);
  //   // // this.chapterstatemanagement.setChapterRefName(result.ChapterRef, result.ChapterName)
  //   return result;
  // }

  //  new login method with all response fields flattened (ObjectTag) and error handling for auth errors
  public async LoginUser(req: UserLoginRequest) {
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/loginsystemuser`;

    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    let request = this.http.post(url, req, { headers: headers });

    let resp = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr = new UserLoginResponse();
        tr.successful = false;
        tr.message = this.BuildErrorMessage(err);
        return tr;
      });

    // ✅ API returns { Successful, ObjectTag: { ...all data... } }
    if (resp && resp.Successful && resp.ObjectTag) {
      const o = resp.ObjectTag; // shorthand

      resp.successful = true;
      resp.message = resp.Message ?? "";
      resp.login_token = o.login_token ?? "";
      resp.login_user_id = o.login_user_id ?? 0;
      resp.post_id = o.post_id ?? 0;
      resp.under_supervision_post_ref = o.under_supervision_post_id ?? 0;
      resp.user_display_name = o.user_display_name ?? "";
      resp.first_login_completed = o.first_login_completed ?? 0;
      resp.role = o.role ?? 0;
      resp.role_name = o.role_name ?? "";
      resp.language_preference = o.language_preference ?? 0; // ✅ fixed typo
      resp.phc_name_en = o.phc_name_en ?? "";
      resp.phc_name_mr = o.phc_name_mr ?? "";
      resp.sc_name_en = o.sc_name_en ?? "";
      resp.sc_name_mr = o.sc_name_mr ?? "";
      resp.village_name_en = o.village_name_en ?? "";
      resp.village_name_mr = o.village_name_mr ?? "";
      resp.user_display_name_en = o.user_display_name_en ?? "";
      resp.user_display_name_mr = o.user_display_name_mr ?? "";
      resp.role_name_en = o.role_name_en ?? "";
      resp.role_name_mr = o.role_name_mr ?? "";
      resp.financial_year_responses = o.financial_year_responses ?? [];
      resp.months = o.months ?? [];
      resp.valid_web_menu_item_ids = o.valid_web_menu_item_ids ?? []; // ✅ correct key now
    } else if (resp && !resp.Successful) {
      resp.successful = false;
      resp.message = resp.Message ?? resp.message ?? "Login failed";
    }

    let result = Object.assign(
      new UserLoginResponse(),
      resp,
    ) as UserLoginResponse;

    this.sessionValues.CurrentLoginToken = result.login_token;
    this.sessionValues.UserDisplayName = result.user_display_name;
    this.appStateManagement.StorageKey.setItem(
      "LoginUserRef",
      result.login_user_id.toString(),
    );
    this.appStateManagement.StorageKey.setItem(
      "ToDate",
      result.to_date.toString(),
    );
    this.appStateManagement.StorageKey.setItem("Role", result.role.toString());

    return result;
  }

  public async LanguagePreferenceUser(req: UserLanguagePreferenceRequest) {
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/updateuserlanguagepreference`;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, req, { headers: headers });
    let resp = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr = new UpdateUserLanguageResponse();
        tr.Successful = false;
        // tr.Message = err.statusText;
        tr.Message = this.BuildErrorMessage(err);
        return tr;
      });

    let result = Object.assign(
      new UpdateUserLanguageResponse(),
      resp,
    ) as UpdateUserLanguageResponse;
    // this.sessionValues.CurrentLoginToken = result.LoginToken;
    // this.sessionValues.UserDisplayName = result.UserDisplayName;
    // this.appStateManagement.setValidMenuItemIds2(result.ValidMenuItemIds2);

    // this.appStateManagement.StorageKey.setItem('ChapterRef', result.ChapterRef.toString());
    // this.appStateManagement.StorageKey.setItem('SubChapterRef', result.SubChapterRef.toString());

    return result;
  }

  public async LogoutUser(req: UserLogoutRequest) {
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/logoutsystemuser`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, req, { headers: headers });
    let resp = await request
      .toPromise()
      .then((resp) => resp as TransactionResult)
      .catch((err) => {
        let tr: TransactionResult = {
          Successful: false,
          // Message: err.statusText,
          Message: this.BuildErrorMessage(err)
        };
        return tr;
      });

    let result: TransactionResult = {
      Successful: resp.Successful,
      Message: resp.Message,
    };
    if (result.Successful) {
      // this.sessionValues.ClearAllStateValues();
      // this.appStateManagement.clearValidMenuItemIds2();
      // this.appStateManagement.StorageKey.clear();

      this.ResetDataAfterLogout();
    }

    return result;
  }

  public ResetDataAfterLogout(){
    this.sessionValues.ClearAllStateValues();
    this.appStateManagement.clearValidMenuItemIds2();
    this.appStateManagement.StorageKey.clear();
  }

  public async ChangeLoggedInUserPassword(req: UserLogoutRequest) {
    let apiRoot = this.sessionValues.cloudUIController;
    let url = `${apiRoot}/changeloggedinuserpassword`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, req, { headers: headers });

    let resp = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr: TransactionResult = {
          Successful: false,
          // Message: err.statusText,
          Message: this.BuildErrorMessage(err)
        };
        return tr;
      });

    let result: TransactionResult = {
      Successful: resp.Successful,
      Message: resp.Message,
    };

    // this.sessionValues.ClearAllStateValues();
    this.appStateManagement.clearValidMenuItemIds2();

    return resp;
  }

  public async FetchRequestForForgotPassword(endPoint: string, body: any) {
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/${endPoint}`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, body, { headers: headers });

    let resp = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr = new UserLoginResponse();
        tr.successful = false;
        // tr.message = err.statusText;
        tr.message = this.BuildErrorMessage(err);
        return tr;
      });

    // ✅ Add this mapping
    if (resp) {
      resp.successful = resp.Successful ?? false;
      resp.message = resp.Message ?? "";
    }

    let result = Object.assign(
      new UserLoginResponse(),
      resp,
    ) as UserLoginResponse;
    return result;
  }

  public async VerifyOTP(endPoint: string, body: any) {
    // let apiRoot = this.sessionValues.generateuserotp;
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/verifyuserotp`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, body, { headers: headers });

    let resp = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr = new UserLoginResponse();
        tr.successful = false;
        // tr.message = err.statusText;
        tr.message = this.BuildErrorMessage(err);
        return tr;
      });
        // ✅ Add this mapping
  if (resp) {
    resp.successful = resp.Successful ?? false;
    resp.message = resp.Message ?? "";
  }


    let result = Object.assign(
      new UserLoginResponse(),
      resp,
    ) as UserLoginResponse;
    return result;
  }

  public async UpdatePassword(endPoint: string, body: any) {
    // let apiRoot = this.sessionValues.generateuserotp;
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/updatepassword`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, body, { headers: headers });

    let resp = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr = new UserLoginResponse();
        tr.successful = false;
        // tr.message = err.statusText;
        tr.message = this.BuildErrorMessage(err);
        return tr;
      });
        // ✅ Add this mapping
  if (resp) {
    resp.successful = resp.Successful ?? false;
    resp.message = resp.Message ?? "";
  }

    let result = Object.assign(
      new UserLoginResponse(),
      resp,
    ) as UserLoginResponse;
    return result;
  }

  public async SaveSuperUser(body: any) {
    let apiRoot = this.sessionValues.userManagement;
    let url = `${apiRoot}/savecustomeruser/${this.sessionValues.CurrentLoginToken}/${this.sessionValues.LoginDeviceId}`;
    // let body = {}

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.post(url, body);

    let pktResult = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr: TransactionResult = {
          Successful: false,
          // Message: err.statusText,
          Message: this.BuildErrorMessage(err)
        };

        return;
        tr;
      });

    if (!pktResult.Successful) {
      let result = pktResult as TransactionResult;
      return result;
    }
    if (pktResult.Successful) {
      let result = pktResult as TransactionResult;
      return result;
    }

    return null as any;
  }

// Refresh the domain cache on the server
  public async RefreshDomainCache(): Promise<TransactionResult> {
  try {
    let apiRoot = this.sessionValues.requestController;
    let url = `${apiRoot}/refreshdomaincache`;

    let request = this.http.post(url, {});

    let pktResult = await request
      .toPromise()
      .then((resp) => resp as TransactionResult)
      .catch((err) => {
        let tr: TransactionResult = {
          Successful: false,
          // Message: err.statusText || "Server error",
          Message: this.BuildErrorMessage(err)
        };
        console.log('tr :', tr);
        return tr; // ✅ must return
      });

    return pktResult;
  } catch (error) {
    return {
      Successful: false,
      Message: "Unexpected error",
    } as TransactionResult;
  }
}

  public async GetSuperUser(projectRef: number) {
    let apiRoot = this.sessionValues.userManagement;
    let url = `${apiRoot}/customeruserlist/${projectRef}/${this.sessionValues.CurrentLoginToken}/${this.sessionValues.LoginDeviceId}`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    let request = this.http.get(url);

    let pktResult = await request
      .toPromise()
      .then((resp) => resp as any)
      .catch((err) => {
        let tr: TransactionResult = {
          Successful: false,
          // Message: err.statusText,
          Message: this.BuildErrorMessage(err)
        };

        return;
        tr;
      });

    if (!pktResult.Successful) {
      let result = pktResult as TransactionResult;
      return result;
    }
    if (pktResult.Successful) {
      let result = pktResult as TransactionResult;
      return result;
    }

    return null as any;
  }

  public async GetDateTimeIntegerValue(
    apiRoot: string,
    dateString: string,
  ): Promise<number> {
    this.loader.show();
    try {
      let parts = dateString.split("/");
      let year = +parts[0];
      let month = +parts[1];
      let day = +parts[2];

      let url = `${apiRoot}/datetimeintegervalue/${year}/${month}/${day}`;

      let request = this.http.get(url);

      let result = await request
        .toPromise()
        .then((resp) => resp as number)
        .catch((_err) => 0);

      return result;
    } catch (error) {
      return -1;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  public async GetDateTimeStringValue(
    apiRoot: string,
    dateValue: number,
  ): Promise<string> {
    this.loader.show();
    try {
      let url = `${apiRoot}/datetimestringvalue/${dateValue}`;

      let request = this.http.get(url);

      let result = await request
        .toPromise()
        .then((resp) => resp as string)
        .catch((err) => err);

      return result;
    } catch (error) {
      return "";
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  public async GetISOFormatDateTimeStringValue(
    apiRoot: string,
    dateValue: number,
  ): Promise<string> {
    this.loader.show();
    try {
      let url = `${apiRoot}/isoformatdatetimestringvalue/${dateValue}`;

      let request = this.http.get(url);

      let result = await request
        .toPromise()
        .then((resp) => resp as string)
        .catch((err) => err);

      return result;
    } catch (error) {
      return "";
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  public async GetNodeConfig(
    GAAProjectSpaceTypePlannedDeviceConnectionRef: number,
  ): Promise<any> {
    try {
      let apiRoot = this.sessionValues.mqttController;
      let url = `${apiRoot}/nodecontrollableparameters/${GAAProjectSpaceTypePlannedDeviceConnectionRef}`;
      let body = {};

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.get(url);

      let result = await request
        .toPromise()
        .then((resp) => resp as string)
        .catch((err) => err);

      return result;
    } catch (error) {
      return "";
    }
  }

  // public async GetCheckListBySpaceGroup(projectRef: number, spacegroupRef: number): Promise<any> {
  //   try {
  //     let apiRoot = this.sessionValues.guestOperations;
  //     let url = `${apiRoot}/currentcheckinlistbyspacegroup/${projectRef}/${spacegroupRef}/${this.sessionValues.CurrentLoginToken}/${this.sessionValues.LoginDeviceId}`;
  //     let body = {}

  //     const headers = new HttpHeaders({
  //       'Content-Type': 'application/json'
  //     });

  //     let request = this.http.get(url);

  //     let result = await (request.toPromise()
  //       .then(resp => resp as string)
  //       .catch(err => err));

  //     return result;
  //   }
  //   catch (error) {
  //     return '';
  //   }
  // }

  public async GetCurrentTime(): Promise<any> {
    try {
      let apiRoot = this.sessionValues.requestController;
      let url = `${apiRoot}/currentdatetime`;
      let body = {};

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.get(url);

      let result = await request
        .toPromise()
        .then((resp) => resp as string)
        .catch((err) => err);

      return result;
    } catch (error) {
      return "";
    }
  }

  public async GetFinancialYearMonthList(req: FinancialYearMonthRequest) {
    try {
      let apiRoot = this.sessionValues.requestController;
      let url = `${apiRoot}/getfinancialyearmonthlist`;

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.post(url, req, { headers: headers });
      let resp = await request
        .toPromise()
        .then((resp) => resp as any)
        .catch((err) => {
          let tr = new FinancialYearMonthResponse();
          tr.Successful = false;
          // tr.Message = err.statusText;
          tr.Message = this.BuildErrorMessage(err); 
          return tr;
        });

      let result = Object.assign(
        new FinancialYearMonthResponse(),
        resp,
      ) as FinancialYearMonthResponse;
      return result;
    } catch {
      let tr = new FinancialYearMonthResponse();
      tr.Successful = false;
      tr.Message = "Internal Server Error";
      return tr;
    }
  }

  public async GetTransaltedText(req: TranslateTextRequest) {
    try {
      let apiRoot = this.sessionValues.requestController;
      let url = `${apiRoot}/translate`;

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      let request = this.http.post(url, req, { headers: headers });
      let resp = await request
        .toPromise()
        .then((resp) => resp as any)
        .catch((err) => {
          let tr = new TranslateTextResponse();
          tr.Successful = false;
          // tr.Message = `${err.statusText} - ${err.error}`;
          tr.Message = this.BuildErrorMessage(err);
          return tr;
        });

      let result = Object.assign(
        new TranslateTextResponse(),
        resp,
      ) as TranslateTextResponse;
      return result;
    } catch {
      let tr = new TranslateTextResponse();
      tr.Successful = false;
      tr.Message = "Internal Server Error";
      return tr;
    }
  }

  public async GetValidMenuItemList() {
    this.loader.show();
    try {
      let apiRoot = this.sessionValues.requestController;
      let url = `${apiRoot}/web/valid-menu-item-ids`;

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });
      let body = {};
      let request = this.http.post(url, body, { headers: headers });
      console.log("resp sidebar:", request);
      let resp = await request
        .toPromise()
        .then((resp) => resp as any)
        .catch(async (err) => {
          let tr = new GetValidMenuItemResponse();
          console.log("tr sidebar :", tr);
          await this.handleAuthError(err);
          return tr;
        });

      let result = Object.assign(
        new GetValidMenuItemResponse(),
        resp,
      ) as GetValidMenuItemResponse;
      return result;
    } catch {
      let tr = new GetValidMenuItemResponse();
      tr.Message = "Internal Server Error";
      return tr;
    } finally {
      // ✅ Hide loader at end, always runs
      this.loader.hide();
    }
  }

  // public async DownloadDocument( DocumentPath :string, LoginToken:string): Promise<any> {
  //   try {
  //     let apiRoot = this.sessionValues.adminApp;
  //     let url = `${apiRoot}/uploadeddocumentpath/${DocumentPath}/${LoginToken}`;
  //     let body = {}

  //     const headers = new HttpHeaders({
  //       'Content-Type': 'application/json'
  //     });

  //     let request = this.http.get(url);

  //     let result = await (request.toPromise()
  //       .then(resp => resp as string)
  //       .catch(err => err));

  //     return result;
  //   }
  //   catch (error) {
  //     return '';
  //   }
  // }

  // public async DownloadDocument(documentPath: string, loginToken: string): Promise<Blob | null> {
  //   try {
  //     const apiRoot = this.sessionValues.adminApp;
  //     const encodedPath = encodeURIComponent(documentPath);
  //     const url = `${apiRoot}/uploadeddocumentpath/${encodedPath}/${loginToken}`;

  //     const headers = new HttpHeaders({
  //       'Content-Type': 'application/json'
  //     });

  //     const response = await firstValueFrom(this.http.get(url, {
  //       headers: headers,
  //       responseType: 'blob'
  //     }));

  //     return response;
  //   } catch (error) {
  //     console.error('Download failed:', error);
  //     return null;
  //   }
  // }

  public async FetchStringResult(url: string): Promise<string> {
    try {
      let request = this.http.get(url);

      let result = await request
        .toPromise()
        .then((resp) => resp as string)
        .catch((err) => err.error.text);
      return result;
    } catch (error) {
      return error as string;
    }
  }
  private isPopupOpen = false;

  private async handleAuthError(error: any): Promise<boolean> {
    if (this.isPopupOpen) return true;
    // if (error.statusText === 'OK') return false;
    // Only handle auth errors
    if (error.status !== 440 && error.status !== 401) return false;

    this.isPopupOpen = true;

    let payload: any = null;

    if (error.error?.Payload) {
      try {
        payload = JSON.parse(error.error.Payload);
      } catch (e) {
        console.log("Payload parse error:", e);
      }
    }

    const tag = payload?.Tag?.replace(/"/g, "");

    if (tag === "SessionExpired" || tag === "Unauthorized") {
      await this.showErrorMessageForSessionExpire(
        tag === "Unauthorized" ? "Unauthorized" : "Session Expired",
        payload?.Message || "Please login again.",
      );

      // Wait a microtask to ensure UI closed
      await new Promise((resolve) => setTimeout(resolve, 100));

      let logoutRequest = new UserLogoutRequest();
      logoutRequest.login_token = this.appStateManagement.getLoginToken();
      await this.LogoutUser(logoutRequest);

      await this.router.navigate(["/login-form"]);
    }

    this.isPopupOpen = false;
    return true;
  }

  public async showErrorMessageForSessionExpire(
    title: string,
    msg: string,
  ): Promise<void> {
    await this.ngZone.run(async () => {
      await Swal.fire({
        title: title,
        text: msg,
        icon: "error",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
        backdrop: true,
      });
    });
  }


// Centralized error message builder to handle various HTTP error scenarios
  public BuildErrorMessage(err:HttpErrorResponse){
    if( err.status == 0 || err.statusText == 'Unknown Error'){
      return 'Server Not Responding';
    }
    else if( err.status == HttpStatusCode.Unauthorized ){
      return `${err.statusText} - ${err.error}`
    }
    else if( err.status == HttpStatusCode.UpgradeRequired ){
      return `${err.statusText} - ${err.error}`
    }
    else{
     let msg = err.message || err.error ||'Unknown error' 
     return msg;
    }

  }

  // Alternative more detailed error message builder with specific handling for auth errors and network issues (Claude Code)
//   public BuildErrorMessage(err: HttpErrorResponse | any): string {
//   if (!err) return 'Unknown error';

//   if (err.status === 0 || err.statusText === 'Unknown Error') {
//     return 'Unable to reach server. Please check your connection.';
//   }

//   if (err.status === HttpStatusCode.Unauthorized || err.status === 440) {
//     const detail = typeof err.error === 'string' ? err.error : err.error?.Message || '';
//     return detail ? `${err.statusText} - ${detail}` : err.statusText;
//   }

//   const msg = err.error?.message 
//     || err.error?.Message 
//     || (typeof err.error === 'string' ? err.error : null)
//     || err.message 
//     || 'Unknown error';

//   return msg;
// }

}
